import type React from "react";
import { forwardRef, createContext, useContext, useState } from "react";
import { CheckIcon, ChevronsUpDown } from "lucide-react";
import * as RPNInput from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  cn,
} from "@fluid-app/ui-primitives";

type PhoneInputValue = RPNInput.Value;

type PhoneInputProps = {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  defaultCountry?: string;
  disabled?: boolean;
  error?: boolean;
  searchPlaceholder?: string;
  emptyText?: string;
  onFocus?: () => void;
  onBlur?: () => void;
};

export type { PhoneInputProps, PhoneInputValue };

const PhoneInputInternalContext = createContext<{
  error?: boolean;
}>({});

const toValidCountry = (
  countryCode: string | undefined,
): RPNInput.Country | undefined => {
  if (!countryCode) return undefined;
  const upperCode = countryCode.toUpperCase();
  return RPNInput.getCountries().find((c: RPNInput.Country) => c === upperCode);
};

export function PhoneInput({
  id,
  value,
  onChange,
  defaultCountry = "US",
  disabled = false,
  error = false,
  searchPlaceholder = "Search country...",
  emptyText = "No country found.",
  onFocus,
  onBlur,
}: PhoneInputProps): React.JSX.Element {
  return (
    <PhoneInputInternalContext.Provider value={{ error }}>
      <RPNInput.default
        id={id}
        className="flex"
        flagComponent={FlagComponent}
        countrySelectComponent={CountrySelectComponent}
        international={false}
        defaultCountry={toValidCountry(defaultCountry)}
        smartCaret={false}
        inputComponent={InputComponent}
        value={(value as PhoneInputValue) || undefined}
        onChange={(val: PhoneInputValue | undefined) =>
          onChange?.((val as string) || "")
        }
        disabled={disabled}
        countrySelectProps={{ searchPlaceholder, emptyText }}
        onFocus={onFocus}
        onBlur={onBlur}
      />
    </PhoneInputInternalContext.Provider>
  );
}

const InputComponent = forwardRef<
  HTMLInputElement,
  React.ComponentProps<"input">
>(({ className, ...props }, ref) => {
  const { error } = useContext(PhoneInputInternalContext);
  return (
    <Input
      className={cn(
        "rounded-l-none border-l-0 focus-visible:ring-0 focus-visible:ring-offset-0",
        error && "border-destructive",
        className,
      )}
      {...props}
      ref={ref}
    />
  );
});
InputComponent.displayName = "PhoneInputField";

type CountrySelectProps = {
  disabled?: boolean;
  value: RPNInput.Country;
  options: Array<{ label: string; value: RPNInput.Country | undefined }>;
  onChange: (country: RPNInput.Country) => void;
  searchPlaceholder?: string;
  emptyText?: string;
};

function CountrySelectComponent({
  disabled,
  value: selectedCountry,
  options: countryList,
  onChange,
  searchPlaceholder,
  emptyText,
}: CountrySelectProps): React.JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const { error } = useContext(PhoneInputInternalContext);

  return (
    <Popover
      open={isOpen}
      modal
      onOpenChange={(open) => {
        setIsOpen(open);
        if (open) setSearchValue("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            "flex gap-1 rounded-r-none px-3",
            error && "border-destructive",
          )}
          disabled={disabled}
        >
          <FlagComponent
            country={selectedCountry}
            countryName={selectedCountry}
          />
          <ChevronsUpDown
            className={cn(
              "-mr-2 size-4 opacity-50",
              disabled ? "hidden" : "opacity-100",
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0">
        <Command>
          <CommandInput
            value={searchValue}
            onValueChange={setSearchValue}
            placeholder={searchPlaceholder}
          />
          <CommandList>
            <ScrollArea className="h-72">
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup>
                {countryList.map(({ value, label }) =>
                  value ? (
                    <CommandItem
                      key={value}
                      className="gap-2"
                      onSelect={() => {
                        onChange(value);
                        setIsOpen(false);
                      }}
                    >
                      <FlagComponent country={value} countryName={label} />
                      <span className="flex-1 text-sm">{label}</span>
                      <span className="text-muted-foreground text-sm">{`+${RPNInput.getCountryCallingCode(value)}`}</span>
                      <CheckIcon
                        className={cn(
                          "ml-auto size-4",
                          value === selectedCountry
                            ? "opacity-100"
                            : "opacity-0",
                        )}
                      />
                    </CommandItem>
                  ) : null,
                )}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function FlagComponent({
  country,
  countryName,
}: RPNInput.FlagProps): React.JSX.Element {
  const Flag = flags[country];
  return (
    <span className="bg-foreground/20 flex h-4 w-6 overflow-hidden rounded-sm [&_svg:not([class*='size-'])]:size-full">
      {Flag && <Flag title={countryName} />}
    </span>
  );
}
